/**
 * Model B position registry: which owned subs hold an open position, and in
 * which product section (stake, borrow, trade, …) it was opened.
 *
 * Why it exists: every swap creates a disposable sub, so "all subs" grows
 * without bound. Product pages and the portfolio only need the few subs that
 * carry positions. Losing this list never loses funds: control notes on chain
 * still recover every sub, and settled balances are swept regardless.
 *
 * Privacy: sub addresses are never stored in plaintext. The list is sealed
 * with AES-GCM under a key derived (HKDF, domain-separated) from the account's
 * scan key; the storage slot name is a hash of that secret, so it links to
 * nothing. The deployment is bound as additional authenticated data.
 */
import type { V2KeyContext } from "./keys";

export type PositionEntry = { subwallet: string; section: string };

const DOMAIN = "redacted-v2/model-b/positions/v1";
const encoder = new TextEncoder();

const toBase64 = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...bytes));
const fromBase64 = (value: string): Uint8Array =>
  Uint8Array.from(atob(value), (char) => char.charCodeAt(0));

const aad = (context: Pick<V2KeyContext, "chainId" | "proxyAddress">) =>
  encoder.encode(`${DOMAIN}|${context.chainId}|${context.proxyAddress}`);

const deriveKey = async (scanKey: Uint8Array): Promise<CryptoKey> => {
  const base = await crypto.subtle.importKey(
    "raw",
    scanKey.slice(),
    "HKDF",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: encoder.encode(DOMAIN),
      info: encoder.encode("seal"),
    },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

const slot = async (
  scanKey: Uint8Array,
  context: Pick<V2KeyContext, "chainId" | "proxyAddress">
): Promise<string> => {
  const material = new Uint8Array([
    ...encoder.encode(`${DOMAIN}|slot|`),
    ...scanKey,
    ...aad(context),
  ]);
  const digest = new Uint8Array(
    await crypto.subtle.digest("SHA-256", material)
  );
  material.fill(0);
  return `rd:mb-pos:${[...digest.slice(0, 16)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
};

const valid = (value: unknown): value is PositionEntry[] =>
  Array.isArray(value) &&
  value.length <= 256 &&
  value.every(
    (entry) =>
      entry &&
      typeof entry === "object" &&
      typeof entry.subwallet === "string" &&
      /^[a-z]+1[02-9ac-hj-np-z]{38,90}$/.test(entry.subwallet) &&
      typeof entry.section === "string" &&
      /^[a-z0-9./_-]{0,64}$/.test(entry.section)
  );

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

const defaultStorage = (): Storage | undefined => {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
};

/** Undefined means "never stored" (first Model B session on this device). */
export const loadPositions = async (
  scanKey: Uint8Array,
  context: Pick<V2KeyContext, "chainId" | "proxyAddress">,
  storage: Storage | undefined = defaultStorage()
): Promise<PositionEntry[] | undefined> => {
  const raw = storage?.getItem(await slot(scanKey, context));
  if (!raw) return undefined;
  try {
    const { iv, data } = JSON.parse(raw) as { iv: string; data: string };
    const plain = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: fromBase64(iv), additionalData: aad(context) },
      await deriveKey(scanKey),
      fromBase64(data)
    );
    const parsed: unknown = JSON.parse(new TextDecoder().decode(plain));
    return valid(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const savePositions = async (
  scanKey: Uint8Array,
  context: Pick<V2KeyContext, "chainId" | "proxyAddress">,
  entries: readonly PositionEntry[],
  storage: Storage | undefined = defaultStorage()
): Promise<boolean> => {
  if (!storage || !valid(entries)) return false;
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: aad(context) },
      await deriveKey(scanKey),
      encoder.encode(JSON.stringify(entries))
    )
  );
  try {
    storage.setItem(
      await slot(scanKey, context),
      JSON.stringify({ iv: toBase64(iv), data: toBase64(data) })
    );
    return true;
  } catch {
    return false;
  }
};

/** Most recent entry per section wins; a sub appears once. */
export const upsertPosition = (
  entries: readonly PositionEntry[],
  next: PositionEntry
): PositionEntry[] => [
  ...entries.filter((entry) => entry.subwallet !== next.subwallet),
  next,
];

/** Product scope of a page: its first two path segments, e.g.
 * "/stake/RUJI" -> "stake/ruji", "/borrow/BTC/USDC" -> "borrow/btc". */
export const sectionOf = (pathname: string): string =>
  pathname
    .split("/")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) =>
      decodeURIComponent(part)
        .toLowerCase()
        .replace(/[^a-z0-9._-]/g, "-")
    )
    .join("/")
    .slice(0, 64);

/** Product areas whose pages show an account's open positions. */
const POSITION_PRODUCTS = new Set([
  "stake",
  "borrow",
  "lend",
  "strategies",
  "index",
  "liquidate",
]);

/**
 * The position to show on a page: the latest one opened in that scope. A
 * sub adopted from before Model B (unknown scope) keeps serving position
 * pages until a newer position exists there, so migrated accounts see their
 * existing stakes and loans exactly as before.
 */
export const positionForSection = (
  entries: readonly PositionEntry[],
  section: string
): string | undefined => {
  if (!section.includes("/")) return undefined;
  const exact = [...entries]
    .reverse()
    .find((entry) => entry.section === section)?.subwallet;
  if (exact) return exact;
  if (!POSITION_PRODUCTS.has(section.split("/")[0])) return undefined;
  return [...entries].reverse().find((entry) => entry.section === "")
    ?.subwallet;
};
